import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { BookOpen, Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import {
  contentTypesColumn,
  hasQuizColumn,
  inUseColumn,
  lastUpdatedColumn,
  statusColumn,
} from '@/components/CourseModuleColumns'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { useCoursesScope } from '@/pages/CoursesHub/useCoursesScope'
import { deleteEntityCourse, getEntityCourses } from '@/services/entity_courses.service'
import { toast } from '@/utils/helpers'

const STATE_OPTIONS = ['Draft', 'Published', 'Archived']

/**
 * Accounts / Courses — facility- or org-scoped course module list.
 *
 * Route is gated via routes.jsx, so we don't re-check role here. The endpoint
 * returns the scope's own modules (facility also gets global RescueX +
 * org-shared modules); we filter to is_xverse_course=false so this management
 * view shows only custom modules. On the facility side, org-shared modules are
 * surfaced read-only with a "Shared" badge — they're owned + edited at the org.
 */
export const Courses = observer(() => {
  const { isFacility, scopeId, scopeArgs, hubBase } = useCoursesScope()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [sorting, setSorting] = useState([])
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })

  const queryClient = useQueryClient()
  const filters = {
    is_xverse_course: false,
    ...(stateFilter ? { state: stateFilter } : {}),
  }
  const queryKey = ['accounts', 'courses', scopeId, sorting, pagination, searchTerm, stateFilter]

  const { data: courses, isPending } = useQuery({
    queryKey,
    queryFn: () => getEntityCourses({ ...scopeArgs, sorting, pagination, searchTerm, filters }),
    placeholderData: keepPreviousData,
  })

  const { isPending: pendingDelete, mutate: deleteCourseMutation } = useMutation({
    mutationFn: (id) => deleteEntityCourse({ ...scopeArgs, id }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Course deleted', 'success')
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const debounceSearch = useRef(_.debounce(setSearchTerm, 500)).current

  // A module is shared-in (read-only here) when it's owned by an org but we're
  // viewing from a facility. In the org scope the org owns its own modules, so
  // they stay editable.
  const isSharedIn = (row) => isFacility && Boolean(row.organization)

  const goToNew = () => navigate(`${hubBase}/course-modules/new`)
  const goToEdit = (id) => navigate(`${hubBase}/course-modules/${id}`)

  return (
    <>
      <div className="flex flex-row items-center gap-2 md:hidden">
        <Button onClick={goToNew}>Add Course Module</Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            type="search"
            placeholder="Search courses"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          <Select
            value={stateFilter || 'all'}
            onValueChange={(v) => setStateFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-full md:w-40">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(searchTerm || stateFilter) && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setStateFilter('')
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button className="hidden md:block" onClick={goToNew}>
          Add Course Module
        </Button>
      </div>

      {(() => {
        const showEmptyState =
          !isPending && !searchTerm && !stateFilter && (courses?.results?.length ?? 0) === 0
        if (showEmptyState) {
          return (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Course Modules yet</h3>
                <p className="text-muted-foreground mx-auto mb-4 max-w-md text-sm">
                  A Course Module is a lesson your members complete — built from one or more Course
                  Assets (videos, PDFs) plus optional quizzes. You can upload new assets while
                  building a module.
                </p>
                <Button onClick={goToNew}>Create Course Module</Button>
              </CardContent>
            </Card>
          )
        }
        return (
          <DataTable
            columns={[
              {
                accessorKey: 'name',
                id: 'name',
                header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
                cell: ({ row }) =>
                  isSharedIn(row.original) ? (
                    <span className="flex items-center gap-2">
                      {row.original.name}
                      <Badge variant="secondary">Shared</Badge>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="text-action cursor-pointer text-left hover:underline"
                      onClick={() => goToEdit(row.original.id)}
                    >
                      {row.original.name}
                    </button>
                  ),
              },
              statusColumn,
              contentTypesColumn,
              hasQuizColumn,
              inUseColumn,
              lastUpdatedColumn,
              {
                id: 'edit',
                cell: ({ row }) =>
                  isSharedIn(row.original) ? null : (
                    <Button
                      className="place-self-end"
                      variant="outline"
                      size="sm"
                      onClick={() => goToEdit(row.original.id)}
                    >
                      <Pencil className="mr-1 h-4 w-4" />
                      Edit
                    </Button>
                  ),
                size: 80,
              },
              {
                id: 'delete',
                cell: ({ row }) => {
                  // Org-shared modules can't be deleted from the facility — the
                  // org owns them.
                  if (isSharedIn(row.original)) return null
                  // Backend blocks deleting Published/Archived courses with a 400.
                  // Disable the button to make that constraint visible up-front.
                  const blocked = row.original.state !== 'Draft'
                  return (
                    <ConfirmButton
                      confirmContent={`Delete "${row.original.name}"? This cannot be undone.`}
                      confirmTitle="Delete Course"
                      className="place-self-end"
                      variant="destructive"
                      size="sm"
                      loading={pendingDelete}
                      disabled={blocked}
                      title={blocked ? 'Only Draft courses can be deleted' : undefined}
                      onClick={() => deleteCourseMutation(row.original.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </ConfirmButton>
                  )
                },
                size: 80,
              },
            ]}
            data={courses?.results || []}
            loading={isPending}
            pagination={pagination}
            rowCount={courses?.count || 0}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
          />
        )
      })()}
    </>
  )
})
